// import React from "react";
import { handleFormatAmount } from "@/utility/helper";
import type { TableColumn } from "@/components/common/CommonTable";
import type { ISalaryPreviewUser } from "@/interface/attendance.interface";
import type { IPageInfo } from "@/interface/common.interface";

/**
 * Returns column definitions for the Attendance & Summary tab table.
 * Kept as a pure function (not a hook) so it remains easily testable
 * and free of side-effects.
 */
export function getAttendanceColumns(
  paginationData: IPageInfo
): TableColumn<ISalaryPreviewUser>[] {
  const { currentPage, pageSize } = paginationData;

  return [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-xs">
          {String((currentPage - 1) * pageSize + index + 1).padStart(2, "0")}
        </span>
      ),
    },
    {
      key: "employeeCode",
      header: "Emp Code",
      render: (item) => (
        <span className="font-semibold text-slate-700 text-xs">
          {item.user?.employeeCode || "—"}
        </span>
      ),
    },
    {
      key: "name",
      header: "Employee Name",
      render: (item) => (
        <span className="font-bold text-slate-900 text-xs whitespace-nowrap">
          {item.user?.name || "—"}
        </span>
      ),
    },
    {
      key: "totalDays",
      header: "Total Days",
      render: (item) => (
        <span className="font-semibold text-slate-800 text-xs">
          {item.attendanceSummary?.totalDays ?? 0}
        </span>
      ),
    },
    {
      key: "presentDays",
      header: "Present / Half",
      render: (item) => {
        const p = item.attendanceSummary?.attendance?.present ?? 0;
        const h = item.attendanceSummary?.attendance?.halfDay ?? 0;
        return (
          <div className="text-xs">
            <span className="font-bold text-emerald-700">{p} Present</span>
            {h > 0 && (
              <span className="ml-1 text-[11px] text-amber-600 font-medium">
                ({h} Half)
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "absentDays",
      header: "Absent",
      render: (item) => {
        const ab = item.attendanceSummary?.absentDays ?? 0;
        return ab > 0 ? (
          <span className="font-bold text-xs px-2 py-0.5 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
            {ab} Absent
          </span>
        ) : (
          <span className="text-xs text-slate-400">0</span>
        );
      },
    },
    {
      key: "leaveDays",
      header: "Paid / Unpaid Leave",
      render: (item) => {
        const paid = item.attendanceSummary?.paidLeaveDays ?? 0;
        const unpaid = item.attendanceSummary?.unpaidLeaveDays ?? 0;
        return (
          <div className="text-xs">
            <span className="text-slate-700 font-medium">{paid} Paid</span>
            {unpaid > 0 && (
              <span className="ml-1 text-rose-600 font-semibold">
                / {unpaid} Unpaid
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "weekoffHoliday",
      header: "WO / Holiday",
      render: (item) => {
        const wo = item.attendanceSummary?.attendance?.weekOff ?? 0;
        const hol = item.attendanceSummary?.attendance?.holiday ?? 0;
        return (
          <span className="text-xs text-slate-600 font-medium">
            {wo} WO {hol > 0 ? `/ ${hol} Hol` : ""}
          </span>
        );
      },
    },
    {
      key: "deductionDays",
      header: "Deduction Days",
      render: (item) => {
        const totalDed = item.attendanceSummary?.totalDeductionDays ?? 0;
        const absentDed = item.attendanceSummary?.absentDeductionDays ?? 0;
        const leaveDed = item.attendanceSummary?.leaveDeductionDays ?? 0;
        return (
          <div className="text-xs">
            <span
              className={
                totalDed > 0
                  ? "font-bold text-rose-600"
                  : "text-slate-500 font-medium"
              }
            >
              {totalDed} Days
            </span>
            {totalDed > 0 && (
              <p className="text-[10px] text-slate-500">
                ({absentDed} Abs + {leaveDed} Leave)
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: "dailyRate",
      header: "Daily Rate",
      render: (item) => (
        <span className="font-semibold text-slate-700 text-xs">
          {handleFormatAmount(item.attendanceSummary?.dailyRate || 0)}
        </span>
      ),
    },
    {
      key: "attendanceDeduction",
      header: "Att. Deduction",
      render: (item) => {
        const amt =
          item.attendanceSummary?.totalAttendanceDeductionAmount || 0;
        return (
          <span
            className={
              amt > 0
                ? "font-bold text-rose-600 text-xs"
                : "text-slate-400 text-xs"
            }
          >
            {handleFormatAmount(amt)}
          </span>
        );
      },
    },
    {
      key: "grossSalary",
      header: "Gross Earning",
      render: (item) => (
        <span className="font-bold text-emerald-700 text-xs">
          {handleFormatAmount(item.totals?.earning || 0)}
        </span>
      ),
    },
    {
      key: "deductions",
      header: "Total Deductions",
      render: (item) => (
        <span className="font-bold text-rose-600 text-xs">
          {handleFormatAmount(item.totals?.deduction || 0)}
        </span>
      ),
    },
    {
      key: "netSalary",
      header: "Net Salary",
      render: (item) => (
        <span className="font-extrabold text-blue-700 text-xs bg-blue-50/60 px-2 py-1 rounded border border-blue-100">
          {handleFormatAmount(item.totals?.netSalary || 0)}
        </span>
      ),
    },
  ];
}
